import { BLUE_300, BOX_SHADOW_2XL, CORAL_600, GRAY_700, WHITE } from 'constants/styling/theme'
import { FC, ReactNode, forwardRef } from 'react'

import Box from '@mui/material/Box'
import { MUIButton } from 'components/common/MUIButton'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import styles from './ActionBar.module.sass'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** `ActionBarButton`, `ActionBarLabel` and `ActionBarSeparator` */
  children: ReactNode
  /** Hidden below the `sm` breakpoint, where its phone counterpart (a `BottomBar isPhoneOnly`) takes over */
  isHiddenOnPhone?: boolean
  /**
   * `fixed` (default): floats at the bottom centre of the viewport, above a `BottomBar` showing at the same time;
   * `static`: in the flow, placed by its container (e.g. inside a `Modal`)
   */
  position?: 'fixed' | 'static'
  /** `toolbar` for actions on what the page shows; without it the bar is a group named by `aria-label` */
  role?: 'toolbar'
  /** Name of the bar, so screen-reader users know what its actions act on */
  'aria-label'?: string
  /** Additional class on the bar */
  className?: string
}

/**
 * @component The floating dark bar of a page's main actions: buttons, separators and a short status label.
 * A fixed bar inside a transformed ancestor scrolls with it: portal it to `document.body` then.
 * @example
 * <ActionBar role="toolbar" aria-label={t('bar.label')} isHiddenOnPhone>
 *   <ActionBarLabel id="bar-count">{t('bar.approved', { approved, count })}</ActionBarLabel>
 *   <ActionBarSeparator />
 *   <ActionBarButton label={t('bar.approve_all')} icon={<CheckOutlined />} aria-describedby="bar-count" onClick={approveAll} />
 *   <ActionBarButton label={t('bar.review_all')} isPrimary aria-describedby="bar-count" onClick={reviewAll} />
 * </ActionBar>
 */
export const ActionBar: FC<Props> = ({
  children,
  isHiddenOnPhone = false,
  position = 'fixed',
  role,
  'aria-label': ariaLabel,
  className,
}) => (
  <Box
    role={role ?? 'group'}
    aria-label={ariaLabel}
    className={classnames(styles.actionBar, styles[position], { [styles.hiddenOnPhone]: isHiddenOnPhone }, className)}
    sx={{ boxShadow: BOX_SHADOW_2XL }}
  >
    {children}
  </Box>
)

/**
 * @interface ActionBarButtonProps Input properties
 */
export interface ActionBarButtonProps {
  /** Text of the button, its accessible name */
  label: string
  /** Leading glyph, drawn at 1.6rem (e.g. a Material icon) */
  icon?: ReactNode
  /** The bar's main action, a lighter pill */
  isPrimary?: boolean
  /** Inert. With `aria-describedby` it stays in the tab order (`aria-disabled`), so its reason is still read */
  disabled?: boolean
  /** Click action */
  onClick?: () => void
  /** Id of the element saying what the action acts on or why it waits, e.g. an `ActionBarLabel` */
  'aria-describedby'?: string
  /** Disabled, it stays in the tab order (`aria-disabled`): a busy bar keeps the focus of the button just pressed */
  isFocusableWhenDisabled?: boolean
  /** Shows `icon` alone in a square button; `label` stays its accessible name */
  isIconOnly?: boolean
  /** An action that removes something: a coral label and glyph */
  isDestructive?: boolean
}

/** The dark-canvas button types of `MUIButton` */
const TYPE = { primary: 'darkPrimary', plain: 'darkSecondaryNoborder' } as const

/**
 * @component A button of the `ActionBar`, built on `MUIButton`.
 * @example
 * <ActionBarButton label={t('bar.generate')} icon={<AutoAwesomeOutlined />} isPrimary onClick={openGenerate} />
 */
export const ActionBarButton = forwardRef<HTMLButtonElement, ActionBarButtonProps>(({
  label,
  icon,
  isPrimary = false,
  disabled = false,
  onClick,
  'aria-describedby': ariaDescribedBy,
  isFocusableWhenDisabled = false,
  isIconOnly = false,
  isDestructive = false,
}, ref) => {
  const glyph = icon != null ? <span className={styles.icon} aria-hidden="true">{icon}</span> : undefined
  // icon alone: the label is its accessible name, which MUIButton requires with iconOnly
  const nameProps: { iconOnly: true, 'aria-label': string } | { iconOnly?: false } = isIconOnly ? { iconOnly: true, 'aria-label': label } : {}

  return (
    <MUIButton
      ref={ref}
      type={isPrimary ? TYPE.primary : TYPE.plain}
      size="md"
      {...nameProps}
      disabled={disabled}
      isFocusableWhenDisabled={disabled && (isFocusableWhenDisabled || !!ariaDescribedBy)}
      aria-describedby={ariaDescribedBy}
      startIcon={isIconOnly ? undefined : glyph}
      onClick={() => onClick?.()}
      sx={{
        flexShrink: 0,
        gap: '.4rem',
        paddingInline: isIconOnly ? 0 : '1.2rem',
        fontWeight: 450,
        lineHeight: '2rem',
        whiteSpace: 'nowrap',
        ...(isDestructive && { '&&': { color: CORAL_600 } }),
        '&:focus-visible': { boxShadow: `0 0 0 2px ${BLUE_300}` },
        // the dark types' disabled fill reads brighter than the enabled one on this bar: no fill, a dim label
        '&&:disabled, &&[aria-disabled="true"], &&[aria-disabled="true"]:hover': {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: GRAY_700,
        },
      }}
    >
      {isIconOnly ? glyph : label}
    </MUIButton>
  )
})

ActionBarButton.displayName = 'ActionBarButton'

/**
 * @interface ActionBarLabelProps Input properties
 */
export interface ActionBarLabelProps {
  /** Id, for a button described by it */
  id?: string
  /** A short status: a count, what the main action waits for */
  children: ReactNode
}

/**
 * @component A short status beside the actions of an `ActionBar`.
 * @example
 * <ActionBarLabel id="bar-count">{t('bar.approved', { approved, count })}</ActionBarLabel>
 */
export const ActionBarLabel: FC<ActionBarLabelProps> = ({ id, children }) => (
  <Typography id={id} variant="text-sm" component="span" color={WHITE} className={styles.label}>
    {children}
  </Typography>
)

/**
 * @component A thin rule between groups of actions of an `ActionBar`; decoration.
 */
export const ActionBarSeparator: FC = () => <span className={styles.separator} aria-hidden="true" />
